import React, { useRef, useEffect, useState } from 'react';
import { getPartnerTheme } from '../../../config/partnerThemes';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import FeedbackButton from '../../FeedbackButton';
import HandoffCard from '../../HandoffCard';
import { Send } from 'lucide-react';
import { playCoachVoice, stopCoachVoice, coachVoice, coachVoiceActive } from '../../../utils/coachVoice';

const partnerTheme = getPartnerTheme();

const ChatTab = ({
  onDownloadReply,
  enableListen,
  onOpenVoice,
  userName,
  conversations,
  currentConversationId,
  chatError,
  userInput,
  setUserInput,
  handleSubmit,
  handleKeyPress,
  voice,
  isProcessing,
  setShowShareModal,
  setShareMessage,
  getMindStreamColor,
  activeCoach,
  dxiReward,
  fileInputRef,
  handleFileUpload,
  isUploading
}) => {
  const messagesEndRef = useRef(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [escalationRequested, setEscalationRequested] = useState(false);
  const latestMessageRef = useRef(null);
  // EMIT_VOICE_V1: Listen button for long replies (read aloud in sections, with Pause and Stop)
  const [listen, setListen] = useState({ id: null, st: 'idle', note: '' });
  const msgKey = (m, i) => (m && m.id) ? String(m.id) : 'm' + i;
  const listenMinutes = (t) => Math.max(1, Math.round(String(t || '').length / 900));
  const listenLabel = (m, i) => {
    const key = msgKey(m, i);
    if (listen.id === key) {
      if (listen.st === 'loading') return 'Preparing voice...';
      if (listen.st === 'playing') return '\u23F8 Pause';
      if (listen.st === 'paused') return '\u25B6 Resume';
    }
    return '\uD83D\uDD0A Listen (about ' + listenMinutes(m.content) + ' min)';
  };
  const stopListen = () => { stopCoachVoice(); setListen({ id: null, st: 'idle', note: '' }); };
  const doListen = (m, i) => {
    const key = msgKey(m, i);
    if (listen.id === key && listen.st === 'playing') {
      try { coachVoice.audio.pause(); } catch (e) { /* ignore */ }
      setListen({ id: key, st: 'paused', note: '' });
      return;
    }
    if (listen.id === key && listen.st === 'paused') {
      try { coachVoice.audio.play(); } catch (e) { /* ignore */ }
      setListen({ id: key, st: 'playing', note: '' });
      return;
    }
    if (!coachVoiceActive()) {
      setListen({ id: key, st: 'idle', note: 'Pick a voice under Voice first. The device voice cannot read long replies.' });
      return;
    }
    const len = (m.content || '').length;
    if (len > 6000 && !window.confirm('This reply is long (about ' + listenMinutes(m.content) + ' minutes of audio) and uses your daily voice allowance. Read it aloud?')) return;
    stopCoachVoice();
    setListen({ id: key, st: 'loading', note: '' });
    playCoachVoice(
      m.content,
      () => setListen({ id: key, st: 'playing', note: '' }),
      () => setListen({ id: null, st: 'idle', note: '' }),
      () => setListen({ id: key, st: 'idle', note: 'Voice stopped. The daily voice limit may be reached. Try again tomorrow.' })
    );
  };

  const currentConv = Array.isArray(conversations) && currentConversationId 
    ? conversations.find(c => c.id === currentConversationId)
    : null;

  const messages = currentConv?.messages || [];
  // Render markdown links as clickable
  const renderMessageContent = (text) => {
    if (!text) return text;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const linkText = match[1];
      const linkUrl = match[2];
      parts.push(
        React.createElement('a', {
          key: match.index,
          href: linkUrl,
          style: { color: '#60A5FA', textDecoration: 'underline', cursor: 'pointer', whiteSpace: linkText.length <= 25 ? 'nowrap' : 'normal' },
          onClick: (e) => { e.preventDefault(); window.location.href = linkUrl; }
        }, linkText)
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : text;
  };


  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to latest message (for streaming)
  const scrollToLatestMessage = () => {
    latestMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Auto-scroll when messages change or during streaming
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        // Scroll to the streaming message
        scrollToLatestMessage();
      } else {
        // Scroll to bottom after user message
        scrollToBottom();
      }
    }
  }, [messages, messages.length > 0 ? messages[messages.length - 1]?.content : null]);
  const coachLabel = (partnerTheme.coachLabels && partnerTheme.coachLabels[activeCoach]) || partnerTheme.coachLabel || (activeCoach === 'sage' ? 'SAGE' : 'Emit');
  return (
    <div className="emit-chat-container">
      {chatError && (
        <div className="emit-alert emit-alert-danger">
          {chatError}
        </div>
      )}

      <div className="emit-messages">
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '15px 20px', color: 'white', fontFamily: partnerTheme.partnerName ? 'Georgia, \'Times New Roman\', serif' : 'inherit', letterSpacing: partnerTheme.partnerName ? '0.2px' : 'normal' }}>
            <p style={{ fontSize: '1.4rem', marginBottom: '8px' }}>💭 {userName ? `Hey ${userName}, start a conversation with ${coachLabel}` : `Start a conversation with ${coachLabel}`}</p>
            <p style={{ fontSize: '1.1rem' }}>{partnerTheme.welcome && partnerTheme.welcome.lead ? partnerTheme.welcome.lead : "Ask a question or share what's on your mind..."}</p>
            {partnerTheme.welcome && (partnerTheme.welcome.starters || []).length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', maxWidth: '860px', margin: '20px auto 0', textAlign: 'left' }}>
                {partnerTheme.welcome.starters.map((c) => (
                  <button key={c.title} type="button" onClick={() => handleSubmit(c.prompt)} disabled={isProcessing}
                    style={{ padding: '14px 18px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.22)', borderLeft: `5px solid ${partnerTheme.colors.accent}`, borderRadius: '12px', color: '#ffffff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                    <div style={{ fontSize: '17px', fontWeight: 700, lineHeight: 1.3 }}>{c.title}</div>
                    <div style={{ fontSize: '15px', color: 'rgba(255,255,255,0.8)', marginTop: '4px', lineHeight: 1.4 }}>{c.sub}</div>
                  </button>
                ))}
              </div>
            )}
            {partnerTheme.builtBy && (
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '24px', letterSpacing: '0.02em' }}>Built by MindStreams Technology</p>
            )}
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`emit-message emit-message-${msg.role}`}
              ref={idx === messages.length - 1 && msg.role === 'assistant' ? latestMessageRef : null}
            >
              <div className="emit-message-content">
                <div className="emit-message-header">
                  <strong>{msg.role === 'user' ? 'You' : (coachLabel)}</strong>
                  {msg.mindstream && (
                    <span 
                      className="emit-badge"
                      style={{ 
                        background: `${getMindStreamColor(msg.mindstream)}20`,
                        color: getMindStreamColor(msg.mindstream),
                        marginLeft: '8px'
                      }}
                    >
                      {msg.mindstream}
                    </span>
                  )}
                </div>
                <div className="emit-message-text">
                  {msg.isStreaming ? (
                    <div style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>{msg.content}</div>
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({node, ...props}) => <p style={{ margin: '8px 0' }} {...props} />,
                        a: ({node, ...props}) => (partnerTheme.noLinks
                          ? <span>{props.children}</span>
                          : <a {...props} style={{ color: '#60A5FA', textDecoration: 'underline', cursor: 'pointer' }} onClick={(e) => { e.preventDefault(); window.location.href = props.href; }} />),
                        table: ({node, ...props}) => <table className="emit-md-table" {...props} />,
                        th: ({node, ...props}) => <th {...props} />,
                        td: ({node, ...props}) => <td {...props} />,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  )}
                </div>
                {onDownloadReply && msg.role === 'assistant' && !msg.isStreaming && msg.content && msg.content.trim().length > 100 && (
                  <button type="button" onClick={() => onDownloadReply(msg)}
                    title="Download this reply as a Word document"
                    style={{ marginTop: '8px', marginRight: '8px', padding: '6px 12px', fontSize: '13px', background: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#475569', cursor: 'pointer' }}>
                    ⬇ Download as Word
                  </button>
                )}
                {enableListen && msg.role === 'assistant' && !msg.isStreaming && msg.content && msg.content.trim().length > 100 && (
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '8px', marginRight: '8px' }}>
                    <button type="button" onClick={() => doListen(msg, idx)}
                      disabled={listen.id === msgKey(msg, idx) && listen.st === 'loading'}
                      title="Read this reply aloud in your chosen voice"
                      style={{ minHeight: '40px', padding: '0 16px', fontSize: '15px', fontWeight: 600, background: '#ffffff', border: '1.5px solid #334155', borderRadius: '8px', color: '#1e293b', cursor: 'pointer' }}>
                      {listenLabel(msg, idx)}
                    </button>
                    {listen.id === msgKey(msg, idx) && listen.st !== 'idle' && (
                      <button type="button" onClick={stopListen}
                        style={{ minHeight: '40px', padding: '0 16px', fontSize: '15px', fontWeight: 600, background: '#ffffff', border: '1.5px solid #334155', borderRadius: '8px', color: '#1e293b', cursor: 'pointer' }}>
                        {'\u25A0'} Stop
                      </button>
                    )}
                    {listen.id === msgKey(msg, idx) && listen.note && (
                      <span style={{ fontSize: '15px', color: '#b45309' }}>{listen.note}</span>
                    )}
                  </span>
                )}
                {msg.role === 'assistant' && msg.content && msg.content.trim().length > 100 && (
                  <button
                    onClick={() => {
                      setShareMessage(msg.content);
                      setShowShareModal(true);
                    }}
                    style={{
                      marginTop: '12px',
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, var(--accent-teal), #48C9B0)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>📱</span> Share This Insight
                  </button>
                )}
                {msg.role === 'assistant' && !msg.isStreaming && idx > 0 && idx === messages.length - 1 && (
                  <FeedbackButton sessionId={`emit_${Date.now()}`} messageIndex={idx} />
                )}
              </div>
            </div>
          ))
        )}
        {partnerTheme.escalation && messages.length > 0 && (
          <HandoffCard theme={partnerTheme} />
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area with Voice Controls */}
      <div className="emit-input-container">
        {dxiReward && (
          <div
            key={dxiReward.id}
            style={{
              position: 'absolute', right: '14px', bottom: '54px',
              pointerEvents: 'none', zIndex: 40,
              fontSize: '15px', fontWeight: 800,
              color: dxiReward.stream === 'Power' ? '#4f46e5'
                   : dxiReward.stream === 'Flow' ? '#dc2626' : '#059669',
              textShadow: '0 1px 3px rgba(255,255,255,0.95)',
              animation: 'dxiFloat 2.2s ease-out forwards',
              whiteSpace: 'nowrap',
            }}
          >
            {`+${dxiReward.amount} ${dxiReward.stream}`}
          </div>
        )}
        <div className="emit-input-wrapper">
          <textarea
            className="emit-input"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="How may I serve you?"
            rows="1"
            disabled={isProcessing}
          />
        </div>
        <div className="emit-button-row">
          {/* Attach button renders only when the upload handler is
              actually wired. Emit does not pass it, so no dead control. */}
          {handleFileUpload && (<>
          {/* Attach PDF Button (+) */}
          <input ref={fileInputRef} type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFileUpload} style={{ display: 'none' }} />
          <button
            type="button"
            className="emit-attach-btn"
            onClick={() => fileInputRef && fileInputRef.current && fileInputRef.current.click()}
            disabled={isUploading || isProcessing}
            style={{
              padding: '12px',
              background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
              border: 'none',
              borderRadius: '50%',
              cursor: (isUploading || isProcessing) ? 'not-allowed' : 'pointer',
              fontSize: '28px',
              lineHeight: 1,
              color: '#fff',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (isUploading || isProcessing) ? 0.5 : 1,
              transition: 'all 0.3s'
            }}
            title="Attach a PDF or Word doc to cite from"
          >
            {isUploading ? '…' : '+'}
          </button>
          </>)}

          {/* Voice Input Button (Microphone) */}
          {voice.isSupported && (
            <button
              type="button"
              className="emit-mic-btn"
              onClick={() => {
                if (voice.isListening) {
                  voice.stopListening();
                  if (voice.transcript.trim()) {
                    handleSubmit();
                  }
                } else {
                  voice.startListening();
                }
              }}
              disabled={isProcessing}
              style={{
                padding: '12px',
                background: voice.isListening
                  ? 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '50%',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: '20px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isProcessing ? 0.5 : 1,
                boxShadow: voice.isListening ? '0 0 0 3px rgba(244,63,94,0.3)' : 'none',
                transition: 'all 0.3s'
              }}
              title={voice.isListening ? 'Click to stop and send' : 'Click to speak'}
            >
              {voice.isListening ? '⏹️' : '🎤'}
            </button>
          )}

          {/* Voice Output Toggle (Speaker) */}
          {voice.isSupported && (
            <button
              type="button"
              className="emit-voice-toggle-btn"
              onClick={() => voice.toggleVoice()}
              disabled={isProcessing}
              style={{
                padding: '12px',
                background: voice.voiceEnabled
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                border: 'none',
                borderRadius: '50%',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: '20px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.3s'
              }}
              title={voice.voiceEnabled ? 'Disable voice responses' : 'Enable voice responses'}
            >
              {voice.voiceEnabled ? '🔊' : '🔇'}
            </button>
          )}

          {onOpenVoice && (
            <button type="button" onClick={onOpenVoice} aria-label="Choose a voice" title="Choose a voice"
              style={{ height: '48px', padding: '0 16px', borderRadius: '24px', border: 'none', background: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)', color: '#ffffff', fontSize: '15px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {'\uD83D\uDDE3\uFE0F'} Voice
            </button>
          )}

          {/* Send Button - Fixed onClick handler */}
          <button
            className="emit-send-btn"
            onClick={() => handleSubmit()}
            disabled={isProcessing || !userInput.trim()}
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatTab;
